import { useState, useEffect } from "react";
import { Calculator, TrendingUp, ArrowRight, Sparkles, BarChart3, DollarSign, Zap, Award, Target } from "lucide-react";
import { Button } from "./ui/button";
import { Link } from "react-router";
import { getApiUrl, getApiHeaders } from '../utils/apiConfig';

const iconMap: { [key: string]: any } = {
  Calculator, TrendingUp, Sparkles, BarChart3, DollarSign, Zap, Award, Target,
};

interface IndustryMultiplier {
  id: string;
  name: string;
  avgCost: number;
  hourlyRate: number;
  hoursPerEmployee: number;
}

// ============================================
// SKELETON
// ============================================

const ROISkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12 space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-40 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-9 bg-gray-200 rounded w-96 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-80 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>

      {/* Calculator card */}
      <div className="bg-white/80 rounded-3xl shadow-2xl border border-white/60 overflow-hidden">
        <div className="grid lg:grid-cols-5 gap-0">

          {/* Left — inputs (3 cols) */}
          <div className="lg:col-span-3 p-8 sm:p-10 space-y-8">
            {/* Header row */}
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-gray-200 rounded-xl relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="space-y-2">
                <div className="h-5 w-40 bg-gray-200 rounded relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
                <div className="h-3 w-24 bg-gray-200 rounded relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
            </div>

            {/* Industry select */}
            <div className="space-y-3">
              <div className="h-4 w-20 bg-gray-200 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-14 w-full bg-gray-200 rounded-2xl relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>

            {/* Videos slider */}
            <div className="space-y-3">
              <div className="flex justify-between">
                <div className="h-4 w-28 bg-gray-200 rounded relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
                <div className="h-8 w-16 bg-gray-200 rounded relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
              <div className="h-3 w-full bg-gray-200 rounded-full relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>

            {/* Employees slider */}
            <div className="space-y-3">
              <div className="flex justify-between">
                <div className="h-4 w-36 bg-gray-200 rounded relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
                <div className="h-8 w-16 bg-gray-200 rounded relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
              <div className="h-3 w-full bg-gray-200 rounded-full relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>
          </div>

          {/* Right — results (2 cols) */}
          <div className="lg:col-span-2 p-8 sm:p-10 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#2F2E5C] space-y-6">
            {/* Title row */}
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 bg-white/20 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-5 w-40 bg-white/20 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>

            {/* Main ROI box */}
            <div className="bg-white/10 rounded-3xl p-6 space-y-4 border border-white/20">
              <div className="h-3 w-28 bg-white/20 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-16 w-36 bg-white/20 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <div className="h-7 w-24 bg-white/20 rounded-full relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
                <div className="h-4 w-20 bg-white/20 rounded relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
            </div>

            {/* Breakdown rows */}
            <div className="space-y-3">
              <div className="h-3 w-28 bg-white/20 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-12 w-full bg-white/10 rounded-xl relative overflow-hidden border border-white/10">
                  <div className="absolute inset-0 shimmer" />
                </div>
              ))}
            </div>

            {/* CTA button */}
            <div className="h-14 w-full bg-white/20 rounded-2xl relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>

            {/* Disclaimer */}
            <div className="h-3 w-3/4 bg-white/20 rounded mx-auto relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>

        </div>
      </div>
    </div>
  </section>
);

// ============================================
// COMPONENT
// ============================================

export function ROIMiniTeaserPremium() {
  const [industry, setIndustry] = useState<string>('');
  const [numVideos, setNumVideos] = useState(10);
  const [employees, setEmployees] = useState(150);
  const [roiData, setRoiData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [industries, setIndustries] = useState<IndustryMultiplier[]>([]);

  useEffect(() => {
    fetchROIData();
  }, []);

  const fetchROIData = async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await fetch(
        getApiUrl(`homepage/roi-calculator?t=${timestamp}`),
        {
          headers: getApiHeaders(),
          cache: 'no-store',
        }
      );

      if (!response.ok) {
        setDefaultData();
        return;
      }

      const result = await response.json();
      if (result.success && result.data) {
        setRoiData(result.data);
        if (result.data.industries && result.data.industries.length > 0) {
          setIndustries(result.data.industries);
          setIndustry(result.data.industries[0].id);
        } else {
          setDefaultData();
        }
      } else {
        setDefaultData();
      }
    } catch (error: any) {
      setDefaultData();
    } finally {
      setLoading(false);
    }
  };

  const setDefaultData = () => {
    const defaultIndustries: IndustryMultiplier[] = [
      { id: 'manufacturing', name: 'Manufacturing', avgCost: 7000, hourlyRate: 65, hoursPerEmployee: 3 },
      { id: 'energy', name: 'Energy & Utilities', avgCost: 8500, hourlyRate: 75, hoursPerEmployee: 4 },
      { id: 'automotive', name: 'Automotive', avgCost: 6500, hourlyRate: 60, hoursPerEmployee: 2.5 },
      { id: 'healthcare', name: 'Healthcare', avgCost: 7500, hourlyRate: 70, hoursPerEmployee: 3 },
      { id: 'aerospace', name: 'Aerospace', avgCost: 9000, hourlyRate: 80, hoursPerEmployee: 4 },
      { id: 'other', name: 'Other Industry', avgCost: 7000, hourlyRate: 70, hoursPerEmployee: 3 }
    ];
    setIndustries(defaultIndustries);
    setIndustry('manufacturing');
  };

  const getIndustryData = () => {
    if (industries.length === 0) {
      return { name: "Manufacturing", avgCost: 7000, hourlyRate: 65, hoursPerEmployee: 3 };
    }
    const currentIndustry = industries.find(ind => ind.id === industry);
    return currentIndustry || industries[0];
  };

  const industryData = getIndustryData();
  const totalInvestment = numVideos * industryData.avgCost;
  const trainingSavings = employees * industryData.hoursPerEmployee * industryData.hourlyRate;
  const salesRevenue = numVideos * 15000;
  const supportSavings = employees * 100;
  const totalBenefits = trainingSavings + salesRevenue + supportSavings;
  const netROI = totalBenefits - totalInvestment;
  const roiPercent = totalInvestment > 0 ? ((netROI / totalInvestment) * 100) : 0;

  if (loading) {
    return <ROISkeleton />;
  }

  return (
    <section id="roi-calculator" className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      {/* Premium Matte Background with Gradient Overlays */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#2F2E5C] via-[#4a487a] to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#D74444] via-[#e85e5e] to-transparent rounded-full blur-3xl" />
      </div>
      <div className="absolute inset-0 opacity-[0.01]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
            {(() => {
              const BadgeIcon = roiData?.badgeIcon && iconMap[roiData.badgeIcon] ? iconMap[roiData.badgeIcon] : Calculator;
              return <BadgeIcon className="w-4 h-4 text-[#E87D3E]" />;
            })()}
            <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
              {roiData?.badgeText || 'ROI Calculator'}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-[#2F2E5C] via-[#2F2E5C] to-[#4a487a] bg-clip-text text-transparent">
            {roiData?.title || 'Calculate Your Return on Investment'}
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {roiData?.subtitle || 'See your potential ROI in 30 seconds. Based on data from 280+ companies.'}
          </p>
        </div>

        {/* Calculator Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/60 overflow-hidden">
          <div className="grid lg:grid-cols-5 gap-0">
            {/* Left Side - Inputs */}
            <div className="lg:col-span-3 p-8 sm:p-10 bg-gradient-to-br from-white/90 via-gray-50/50 to-white/90 backdrop-blur-xl relative overflow-hidden">
              <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, #2F2E5C 1px, transparent 0)', backgroundSize: '40px 40px'}} />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#2F2E5C] to-[#4a487a] rounded-xl flex items-center justify-center shadow-lg">
                    <Calculator className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#2F2E5C] text-lg">Your Business Details</h3>
                    <p className="text-sm text-gray-600">3 quick inputs</p>
                  </div>
                </div>

                <div className="space-y-8">
                  {/* Industry Select */}
                  <div>
                    <label className="block text-sm font-bold text-[#2F2E5C] mb-3 uppercase tracking-wide">Industry</label>
                    <div className="relative">
                      <select
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        className="w-full px-5 py-4 bg-white border-2 border-gray-200 rounded-2xl font-medium text-[#2F2E5C] focus:border-[#D74444] focus:outline-none focus:ring-4 focus:ring-[#D74444]/20 transition-all appearance-none cursor-pointer shadow-sm hover:shadow-md"
                      >
                        {industries.map(ind => (
                          <option key={ind.id} value={ind.id}>{ind.name}</option>
                        ))}
                      </select>
                      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-5 h-5 text-[#2F2E5C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Number of Videos */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-bold text-[#2F2E5C] uppercase tracking-wide">Videos Needed</label>
                      <div className="flex items-center gap-2">
                        <div className="text-3xl font-bold text-[#D74444]">{numVideos}</div>
                        <span className="text-sm text-gray-500">videos</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={numVideos}
                      onChange={(e) => setNumVideos(Number(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer premium-slider"
                      style={{background: `linear-gradient(to right, #D74444 0%, #D74444 ${(numVideos / 30) * 100}%, #e5e7eb ${(numVideos / 30) * 100}%, #e5e7eb 100%)`}}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2 font-medium">
                      <span>1</span>
                      <span className="text-[#D74444]">Sweet spot: 8-12</span>
                      <span>30</span>
                    </div>
                  </div>

                  {/* Employees */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-bold text-[#2F2E5C] uppercase tracking-wide">Employees Impacted</label>
                      <div className="flex items-center gap-2">
                        <div className="text-3xl font-bold text-[#D74444]">{employees}</div>
                        <span className="text-sm text-gray-500">people</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="500"
                      step="10"
                      value={employees}
                      onChange={(e) => setEmployees(Number(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer premium-slider"
                      style={{background: `linear-gradient(to right, #D74444 0%, #D74444 ${((employees - 10) / 490) * 100}%, #e5e7eb ${((employees - 10) / 490) * 100}%, #e5e7eb 100%)`}}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2 font-medium">
                      <span>10</span>
                      <span>500+</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Results */}
            <div className="lg:col-span-2 p-8 sm:p-10 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#2F2E5C] text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#D74444] via-[#e85e5e] to-transparent opacity-20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-[#E8B84D] via-[#E87D3E] to-transparent opacity-15 rounded-full blur-3xl" />
              <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}} />
              
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-8">
                  <Award className="w-6 h-6 text-[#E8B84D]" />
                  <h3 className="font-bold text-white text-lg">Your Estimated ROI</h3>
                </div>

                <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-lg rounded-3xl p-6 mb-6 border border-white/20 shadow-2xl">
                  <div className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-3">Year 1 Net Return</div>
                  <div className="flex items-end gap-3 mb-4">
                    <div className="text-5xl sm:text-6xl font-bold text-white">
                      ${Math.round(netROI / 1000)}K
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#D74444] to-[#e85e5e] rounded-full shadow-lg">
                      <TrendingUp className="w-4 h-4" />
                      <span className="font-bold text-sm">+{roiPercent.toFixed(0)}% ROI</span>
                    </div>
                    <span className="text-sm text-white/70">from ${Math.round(totalInvestment / 1000)}K</span>
                  </div>
                </div>

                <div className="space-y-3 mb-8">
                  <div className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-4">Benefits Include</div>
                  {[
                    { label: 'Training Savings', value: trainingSavings, icon: <BarChart3 className="w-4 h-4" />, gradient: 'from-[#10b981] to-[#059669]' },
                    { label: 'Sales Revenue', value: salesRevenue, icon: <DollarSign className="w-4 h-4" />, gradient: 'from-[#E87D3E] to-[#E8B84D]' },
                    { label: 'Support Savings', value: supportSavings, icon: <Zap className="w-4 h-4" />, gradient: 'from-[#D74444] to-[#e85e5e]' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 bg-gradient-to-br ${item.gradient} rounded-lg flex items-center justify-center shadow-md`}>
                          {item.icon}
                        </div>
                        <span className="text-sm text-white/90 font-medium">{item.label}</span>
                      </div>
                      <span className="font-bold text-white">${Math.round(item.value / 1000)}K</span>
                    </div>
                  ))}
                </div>

                <Link to={roiData?.ctaLink || '/roi'}>
                  <Button className="w-full bg-gradient-to-r from-[#D74444] to-[#e85e5e] hover:from-[#c13939] hover:to-[#d74444] text-white rounded-2xl h-14 gap-3 shadow-2xl text-base font-bold group transition-all duration-300" variant={null}>
                    {roiData?.ctaText || 'Get Full ROI Breakdown'}
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>

                <p className="text-xs text-white/60 text-center mt-4 leading-relaxed">
                  {roiData?.disclaimerText || 'Detailed report with industry benchmarks, timeline analysis & downloadable PDF'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .premium-slider::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #D74444, #c13939);
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(215, 68, 68, 0.4);
          transition: all 0.3s;
          border: 3px solid white;
        }
        .premium-slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 6px 16px rgba(215, 68, 68, 0.5);
        }
        .premium-slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #D74444, #c13939);
          cursor: pointer;
          border: 3px solid white;
          box-shadow: 0 4px 12px rgba(215, 68, 68, 0.4);
          transition: all 0.3s;
        }
        .premium-slider::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 6px 16px rgba(215, 68, 68, 0.5);
        }
      `}</style>
    </section>
  );
}